// MultiRecordEngine.js — MultiRecord++ recording engine.
//
// We do NOT roll our own recorder anymore. This headless component mounts the
// PROVEN classic recorder (api/Recorder.js AudioRecorder) and drives it from
// the schedule auto-switcher: it feeds the live target set as conversationIds
// (AudioRecorder re-targets + cuts a chunk whenever the set changes — exactly
// the multi-record path the classic recorder already ships) and starts/stops
// it via the recorder's imperative handle in sync with the recording day.
//
// Mounted in the always-present Shell (not the tray) so recording continues
// while the panel is collapsed.
//
// With multirecord_context_and_error_prevention enabled, web drives agent/multiRecorder.js, which
// cuts near 2 minutes, freezes targets per chunk, and posts to the sliding-window
// segmenter. With the flag off, the classic recorder keeps its existing uploads.
// Native still mounts the classic AudioRecorder because it is the bridge to the
// OS recorder — a second WebView getUserMedia fights the single iOS
// AVAudioSession and kills the recording — but that recorder posts to the same
// segmenter endpoint only when the flag is enabled.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import AudioRecorder from 'api/Recorder';
import { GlobalContext } from 'api/GlobalContext';
import { liveStore } from 'agent/liveStore';
import { computeActiveSet } from 'agent/multiRecord';
import { MultiRecordContext } from 'agent/multiRecordContext';
import { MultiRecorder } from 'agent/multiRecorder';
import { isNativeRecorder } from 'api/nativeRecorder';

// Survives a reload; scoped to this browser only (see multiRecordContext.js).
// Namespaced PER USER: the window holds patient transcript text, and nothing in
// logout clears it (removeSessionKey and cacheManager.clear both leave unknown
// keys alone), so a shared workstation would otherwise hand one clinician's
// patients to whoever signs in next.
const contextStorageKey = (email) =>
  `comprehendpt_multirecord_context:${String(email || 'anon').toLowerCase()}`;

const LOG = '[MR++] [Engine]';

const sameSet = (a, b) =>
  a.length === b.length && a.every((x) => b.includes(x));

const MultiRecordEngine = () => {
  const recRef = useRef(null);
  const { apiClient } = useContext(GlobalContext);
  // Wait for the profile, then keep one recorder implementation per mount.
  const [contextEnabled, setContextEnabled] = useState(() =>
    apiClient.name == null && apiClient.onProfileUpdated
      ? null
      : apiClient.features?.multirecord_context_and_error_prevention === true,
  );
  useEffect(() => {
    if (contextEnabled !== null) return undefined;
    const selectRecorder = () => {
      if (apiClient.name == null) return;
      setContextEnabled((previous) => previous === null
        ? apiClient.features?.multirecord_context_and_error_prevention === true
        : previous);
    };
    apiClient.onProfileUpdated(selectRecorder);
    selectRecorder();
    return () => apiClient.offProfileUpdated(selectRecorder);
  }, [apiClient, contextEnabled]);
  // Native always drives the OS recorder through the classic bridge.
  const isNative = isNativeRecorder();
  const useWebSegmenter = contextEnabled && !isNative;
  // String ids (stable Set semantics) — the source the timeline/rail highlight.
  const [activeIds, setActiveIds] = useState([]);
  const [shouldRun, setShouldRun] = useState(false);

  useEffect(() => {
    console.log(
      `${LOG} configured: native=${isNative}, recorder=${useWebSegmenter ? 'web' : 'native bridge'}`,
    );
  }, [isNative, useWebSegmenter]);

  // Recompute the active set on a 1s tick + on every store change, so the
  // auto-switcher follows the schedule clock. Only commit when the set really
  // changes (stable reference) to avoid churning the recorder's target prop.
  useEffect(() => {
    const recompute = () => {
      const s = liveStore.getState();
      const showingTimeline =
        s.trayMode === 'multirecord' || s.panelView === 'timeline';
      const run = showingTimeline && s.recordingDay && !s.micKilled;
      const ids = run
        ? computeActiveSet(s.schedule, Date.now(), s.manualOverrides, {
            beforeMs: (s.bufferBeforeMin || 0) * 60000,
            afterMs: (s.bufferAfterMin || 0) * 60000,
            auto: s.autoSwitch,
          })
        : [];
      setShouldRun((prev) => (prev === run ? prev : run));
      setActiveIds((prev) => (sameSet(prev, ids) ? prev : ids));
    };
    recompute();
    const unsub = liveStore.subscribe(recompute);
    const t = setInterval(recompute, 1000);
    return () => {
      unsub();
      clearInterval(t);
    };
  }, []);

  // Reflect what's actually recording for the timeline red cards + rail chips.
  useEffect(() => {
    liveStore.setRecordingSessions(shouldRun ? activeIds : []);
  }, [shouldRun, activeIds]);

  // The backend wants numeric conversationIds; keep a stable reference so the
  // recorder's conversationIds effect only fires on a real target change.
  const numericIds = useMemo(
    () => activeIds.map(Number).filter(Number.isFinite),
    [activeIds],
  );

  // Record while the day is on AND there's at least one target. AudioRecorder
  // can't record into "no patient", so gaps between visits pause it; the next
  // visit window resumes it. Edge-trigger start/stop off our own intent flag so
  // we never call stop() on a recorder that was never started (it throws on a
  // null MediaRecorder) — e.g. on mount.
  const startedRef = useRef(false);
  const active = contextEnabled !== null && shouldRun && numericIds.length > 0;
  useEffect(() => {
    console.log(
      `${LOG} intent: shouldRun=${shouldRun}, active=${active}, targets=[${numericIds}], recorder=${useWebSegmenter ? 'web' : 'native bridge'}`,
    );
  }, [shouldRun, active, numericIds, useWebSegmenter]);

  useEffect(() => {
    if (useWebSegmenter) return;
    if (active && !startedRef.current) {
      console.log(`${LOG} starting classic recorder for [${numericIds}]`);
      startedRef.current = true;
      recRef.current?.start();
    } else if (!active && startedRef.current) {
      console.log(`${LOG} stopping classic recorder`);
      startedRef.current = false;
      recRef.current?.stop();
    }
  }, [useWebSegmenter, active, numericIds]);

  // ───────────────── segmenter path (web) ─────────────────
  // One recorder and one sliding window for the life of the mount. The window
  // deliberately outlives a target change: A -> A,B is the handoff, which is
  // exactly when the previous chunk's attribution matters most.
  const segRef = useRef(null);
  useEffect(() => {
    if (!useWebSegmenter) return undefined;
    // Rehydrate the window across a reload, so a refresh mid-visit does not
    // restart attribution cold. `from` drops whatever aged out while away.
    const storageKey = contextStorageKey(apiClient?.sessionEmail);
    let restored = null;
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) restored = JSON.parse(saved);
    } catch (e) {
      // Private mode / quota / corrupt value — start with an empty window.
    }
    const context = MultiRecordContext.from(restored);
    const restoredCount = context.toJSON().length;
    console.log(
      `${LOG} created addSegmented recorder with ${restoredCount} restored context entr${restoredCount === 1 ? 'y' : 'ies'}`,
    );
    const persist = () => {
      try {
        // from() already dropped whatever aged out; write the pruned value back
        // so expired transcript text does not sit in storage indefinitely.
        window.localStorage.setItem(
          storageKey,
          JSON.stringify(context.toJSON()),
        );
      } catch (e) {
        // Storage unavailable; the in-memory window still works.
      }
    };
    // Write the pruned snapshot immediately, so a restore that dropped stale
    // entries takes effect in storage even if no chunk lands this session.
    persist();
    const recorder = new MultiRecorder({
      apiClient,
      context,
      onMicLevel: (level) => liveStore.setMicLevel(level),
      onUtterances: persist,
      onError: (error) =>
        console.error(`${LOG} chunk failed:`, error),
    });
    segRef.current = recorder;

    // Ending the recording day is the real session boundary — the sliding
    // window holds patient transcript text, and nothing else clears it (it is
    // rehydrated from localStorage on every mount). Native does the same on a
    // new recording session.
    const unsubscribeDay = liveStore.subscribe(() => {
      if (!liveStore.getState().recordingDay && context.size) {
        context.reset();
        persist();
      }
    });

    return () => {
      unsubscribeDay();
      console.log(`${LOG} disposing addSegmented recorder`);
      recorder.dispose();
      segRef.current = null;
      liveStore.setMicLevel(0);
    };
  }, [useWebSegmenter, apiClient]);

  // Start, retarget, stop. setTargets cuts the current chunk so a switch lands
  // within seconds, and the chunk in flight keeps the set it was spoken under.
  useEffect(() => {
    if (!useWebSegmenter) return;
    const recorder = segRef.current;
    if (!recorder) return;
    if (active) {
      if (recorder.stopped) {
        console.log(`${LOG} starting addSegmented recorder for [${numericIds}]`);
        recorder.start(numericIds).catch((error) => {
          console.error(`${LOG} could not open the mic:`, error);
        });
      } else {
        console.log(
          `${LOG} applying targets [${numericIds}] to running addSegmented recorder`,
        );
        recorder.setTargets(numericIds);
      }
    } else if (!recorder.stopped) {
      console.log(`${LOG} stopping addSegmented recorder; final chunk will flush`);
      recorder.stop();
    }
  }, [useWebSegmenter, active, numericIds]);

  // Audio-reactive recording wave. AudioRecorder doesn't expose a mic level, so
  // run the SAME lightweight RMS analyser the old custom recorder used and feed
  // liveStore.micLevel — LiveWave snaps to speech and decays in silence off it.
  // A dedicated analyser-only stream (not the recorder's); cosmetic, so if the
  // mic can't be opened the wave just stays calm and recording is unaffected.
  useEffect(() => {
    // The segmenter recorder already runs an analyser for its VAD and reports
    // the level itself — opening a second stream here would be a duplicate mic.
    if (useWebSegmenter) return undefined;
    if (!active) {
      liveStore.setMicLevel(0);
      return undefined;
    }

    // Native (iOS/Android): the OS recorder owns the mic and already reports
    // speech via 'audioActivity' messages. Opening a second WebView
    // getUserMedia stream here would fight the single iOS AVAudioSession and
    // kill the native recording — which surfaces to the user as a bogus
    // "interrupted by a phone call" alert. So on native we drive the wave off
    // those messages instead of opening our own stream.
    if (isNativeRecorder()) {
      const onMessage = (e) => {
        if (typeof e?.data !== 'string') return;
        try {
          const msg = JSON.parse(e.data);
          if (msg && msg.type === 'audioActivity') {
            liveStore.setMicLevel(msg.value && msg.value.isSpeaking ? 0.05 : 0);
          }
        } catch (_) {
          // Non-JSON / unrelated message — ignore.
        }
      };
      document.addEventListener('message', onMessage);
      return () => {
        document.removeEventListener('message', onMessage);
        liveStore.setMicLevel(0);
      };
    }

    let audioCtx = null;
    let stream = null;
    let timer = null;
    let cancelled = false;
    navigator.mediaDevices
      ?.getUserMedia({ audio: true })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        timer = setInterval(() => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
          liveStore.setMicLevel(Math.sqrt(sum / buf.length));
        }, 120);
      })
      .catch(() => {
        // No mic for the meter — wave stays calm; recording is unaffected.
      });
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
      audioCtx?.close().catch(() => {});
      liveStore.setMicLevel(0);
    };
  }, [useWebSegmenter, active]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        opacity: 0,
      }}
    >
      {contextEnabled === null || useWebSegmenter ? null : (
        <AudioRecorder
          ref={recRef}
          icon
          multirecordContext={contextEnabled}
          // Single target -> classic single-convo upload (conversationID);
          // 2+ targets -> addConversationAudioMultiple + splitTranscripts.
          conversationID={numericIds.length === 1 ? numericIds[0] : null}
          conversationIds={numericIds}
          isDisabled={numericIds.length === 0}
          seconds_transcribed={0}
          refresh={() => {}}
          setStatus={() => {}}
        />
      )}
    </div>
  );
};

export default MultiRecordEngine;
